export type Tier = 'minis' | 'infantil' | 'menores' | 'juveniles' | 'mayores'

export interface Volume {
  number: number
  title: string
  ages: string
  weeks: string
  topics: string
  tier: Tier
  dot: string
}

export const tierLabels: Record<Tier, string> = {
  minis: 'Minis',
  infantil: 'Infantil',
  menores: 'Menores',
  juveniles: 'Juveniles',
  mayores: 'Mayores',
}

export const volumes: Volume[] = [
  {
    number: 1,
    title: 'Minis Foundation',
    ages: 'Ages 4–6',
    weeks: '17 structured practice weeks',
    topics: 'Fear Removal · First Throws · Tee Contact · Base Running Basics',
    tier: 'minis',
    dot: '🟢',
  },
  {
    number: 2,
    title: 'Minis Consolidation',
    ages: 'Ages 4–6',
    weeks: '18 structured practice weeks',
    topics: 'Partner Drills · Soft Toss · Multi-Base Reads · Relay Concepts',
    tier: 'minis',
    dot: '🟢',
  },
  {
    number: 3,
    title: 'Minis Season Finale',
    ages: 'Ages 4–6',
    weeks: '17 structured practice weeks',
    topics: 'Live Pitching Intro · Position Basics · Scrimmages · Season Portfolio',
    tier: 'minis',
    dot: '🟢',
  },
  {
    number: 4,
    title: 'Infantil Foundation',
    ages: 'Ages 7–9',
    weeks: '17 structured practice weeks',
    topics: 'Crow Hop · Machine Pitch Entry · Defensive Assignments · Live Sequences',
    tier: 'infantil',
    dot: '🔵',
  },
  {
    number: 5,
    title: 'Infantil Tactical Upgrade',
    ages: 'Ages 7–9',
    weeks: '18 structured practice weeks',
    topics: 'Pitching Mechanics · Double Play · Offensive Situations · Tactical Scrimmages',
    tier: 'infantil',
    dot: '🔵',
  },
  {
    number: 6,
    title: 'Infantil Season Finale',
    ages: 'Ages 7–9',
    weeks: '17 structured practice weeks',
    topics: 'Competition Intro · Full Game Format · Catcher Skills · Season Portfolio',
    tier: 'infantil',
    dot: '🔵',
  },
  {
    number: 7,
    title: 'Menores Foundation',
    ages: 'Ages 10–12',
    weeks: '17 structured practice weeks',
    topics: 'Live Pitching · Regulation Distance · Lateral Agility · Hip Separation',
    tier: 'menores',
    dot: '🟣',
  },
  {
    number: 8,
    title: 'Menores Tactical Upgrade',
    ages: 'Ages 10–12',
    weeks: '18 structured practice weeks',
    topics: 'Bunt Tactics · Pickoff Moves · Outfield Routes · Sacrifice Fly',
    tier: 'menores',
    dot: '🟣',
  },
  {
    number: 9,
    title: 'Menores Season Finale',
    ages: 'Ages 10–12',
    weeks: '17 structured practice weeks',
    topics: 'Rundown Execution · 3-2 Count Competition · Game Situations · Portfolio',
    tier: 'menores',
    dot: '🟣',
  },
  {
    number: 10,
    title: 'Juveniles Foundation',
    ages: 'Ages 13–15',
    weeks: '17 structured practice weeks',
    topics: 'Pre-AB Plan · EV Baseline · Mental Reset Protocol · 10-Second Reset',
    tier: 'juveniles',
    dot: '🟠',
  },
  {
    number: 11,
    title: 'Juveniles Tactical Upgrade',
    ages: 'Ages 13–15',
    weeks: '18 structured practice weeks',
    topics: '5-Pitch Arsenal · Shift Defense · Pitch Recognition · SS Range Drills',
    tier: 'juveniles',
    dot: '🟠',
  },
  {
    number: 12,
    title: 'Juveniles Season Finale',
    ages: 'Ages 13–15',
    weeks: '17 structured practice weeks',
    topics: 'Squeeze Play · 1st & 3rd Tactics · Championship Season · Portfolio',
    tier: 'juveniles',
    dot: '🟠',
  },
  {
    number: 13,
    title: 'Mayores Foundation',
    ages: 'Ages 16+',
    weeks: '17 structured practice weeks',
    topics: 'EV Benchmark · 60-Yd Dash · PCR Protocol · Fastball Velocity Baseline',
    tier: 'mayores',
    dot: '🔴',
  },
  {
    number: 14,
    title: 'Mayores Elite Development',
    ages: 'Ages 16+',
    weeks: '18 structured practice weeks',
    topics: 'Pitch Sequencing · Showcase Video · Recruitment Profile · Biomechanics',
    tier: 'mayores',
    dot: '🔴',
  },
  {
    number: 15,
    title: 'Mayores Championship Season',
    ages: 'Ages 16+',
    weeks: '17 structured practice weeks',
    topics: 'Championship Scrimmage · Final Portfolio · Graduation Ceremony · USA Ready',
    tier: 'mayores',
    dot: '🔴',
  },
]

export const volumeIncludes = [
  'Annual Block Planning',
  'Complete Practice Sessions',
  'Plug-and-Play Drill Cards',
  'Skill Progressions',
  'Player Assessment Tools',
  'Printable Practice Sheets',
  'Progress Tracking System',
  'Parent Communication Scripts',
  'Season Portfolio Card',
]

export const ageCoverage = [
  '⚾ Minis · Ages 4–6',
  '⚾ Infantil · Ages 7–9',
  '⚾ Menores · Ages 10–12',
  '⚾ Juveniles · Ages 13–15',
  '⚾ Mayores · Ages 16+',
]

export const problems = [
  "You arrive at the field without a real plan",
  'You spend hours preparing every session from scratch',
  'You keep searching for new drills online every week',
  "Skill levels in your group vary wildly and you can't keep up",
  "You don't know how to structure a full season progression",
  'Your sessions always feel improvised — even after years of coaching',
  'Parents ask "what is my child working on?" and you struggle to answer',
  'You freeze in the dugout when the game gets tight and high-pressure',
]

export interface Bonus {
  id: string
  value: string
  title: string
  description: string
  image: string
}

export const bonuses: Bonus[] = [
  {
    id: 'BONUS 01',
    value: '$59 value',
    title: 'The Level Finder Assessment System',
    description:
      'Assess any player in 10 minutes and place them in the exact right starting block. Includes printable assessment cards for all 5 age categories. Works on Day 1 — no prior experience required.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/a884fe12-2db6-49fc-9a21-c9047c017336.jpg',
  },
  {
    id: 'BONUS 02',
    value: '$54 value',
    title: 'Quick Conditioning & Arm Care Routines',
    description:
      '5 ready-to-run warm-up and conditioning routines for all age groups. Includes the Baseball 360 Arm Care Protocol — the system that prevents the most common youth arm injuries and builds arm strength progressively.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/e6d030b7-0287-44aa-aee4-19affc666f68.jpg',
  },
  {
    id: 'BONUS 03',
    value: '$49 value',
    title: 'Player Fuel & Recovery Nutrition Guide',
    description:
      'The exact pre-game, during-game, and post-game nutrition protocols for youth athletes. Includes a parent-facing handout to distribute at the start of the season. Improves player energy and reduces practice-day absences.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/89d45d39-ee4c-4c36-86ae-657f6aa60111.jpg',
  },
  {
    id: 'BONUS 04',
    value: '$64 value',
    title: 'The Emergency Drills Coaching Toolkit',
    description:
      '20 high-engagement drills that require zero equipment and zero setup. For rain delays, shortened sessions, mixed groups, and the days when nothing goes to plan. Every drill is ready to deploy in under 60 seconds.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/ab4bf334-3578-4a4b-a399-5d15bc43d037.jpg',
  },
  {
    id: 'BONUS 05',
    value: '$59 value',
    title: 'The Weekly Admin Organizer (Full Season Planner)',
    description:
      'A complete 52-week planning system with weekly session sheets, block tracking tools, attendance logs, and the Sunday Night 15-minute prep protocol. Know exactly what you are coaching every day of the year — before Monday arrives.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/bca82477-c43a-45a0-aeb0-24cb1a7bb2ff.jpg',
  },
  {
    id: 'BONUS 06',
    value: '$43 value',
    title: 'The Irresistible Enrollment Scripts (WhatsApp & In-Person)',
    description:
      '24 ready-to-send WhatsApp scripts for acquiring new students, enrolling parents with confidence, retaining players through Month 2, and recovering dropouts. In English and Spanish. The complete business layer for your Baseball 360 program.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/852d8176-59ca-404d-8879-bc2f011a3fca.jpg',
  },
]

export interface Testimonial {
  quote: string
  name: string
  location: string
}

export const heroTestimonials: Testimonial[] = [
  {
    quote:
      'I planned my entire season in a single evening. Now every practice has a clear objective and my players know exactly where they are heading.',
    name: 'Coach Marcus T.',
    location: 'Miami, Florida',
  },
  {
    quote:
      'My players improved more in 2 months than in the entire previous season. The progression is logical and the kids can feel it. Parents are asking me what changed.',
    name: 'Coach Roberto A.',
    location: 'Monterrey, México',
  },
  {
    quote:
      'I used to spend hours on YouTube searching for drills. Now I open Baseball 360 and everything is there — organized, progressive, and ready to run.',
    name: 'Coach David L.',
    location: 'Bogotá, Colombia',
  },
]

export const testimonials: Testimonial[] = [
  {
    quote:
      'I mapped out my whole season in one night. Every single practice now has a clear goal and I walk in knowing exactly what we are doing. My players can feel the difference.',
    name: 'Carlos S.',
    location: 'Houston, Texas',
  },
  {
    quote:
      'My team improved faster in two months than in the entire previous season. The progression just makes sense and parents are finally seeing results they can measure.',
    name: 'Miguel R.',
    location: 'Guadalajara, México',
  },
  {
    quote:
      "I used to spend hours online searching for drills. Now I open Baseball 360 and everything I need is right there — organized by age, by block, by skill. It's a game changer.",
    name: 'Sandra L.',
    location: 'Bogotá, Colombia',
  },
  {
    quote:
      'The player portfolios completely changed my conversations with parents. I finally talk in numbers — exit velocity, block progression — and they trust the process completely.',
    name: 'David T.',
    location: 'Miami, Florida',
  },
  {
    quote:
      'As a first-year coach I was completely overwhelmed. Baseball 360 gave me a full system from Day 1. My confidence on the field doubled within the first week.',
    name: 'Eduardo W.',
    location: 'San Juan, Puerto Rico',
  },
  {
    quote:
      'The Game-Day Strategist saved me in three close games this season. Having those 50 scenarios answered in the dugout is the most valuable thing I\u2019ve ever had as a coach.',
    name: 'Ricardo B.',
    location: 'Caracas, Venezuela',
  },
]

export interface ComparisonRow {
  label: string
  without: string
  with: string
}

export const comparisonRows: ComparisonRow[] = [
  { label: 'Arriving at the field', without: 'No real plan', with: '10 min/week. Done.' },
  { label: 'Finding new drills', without: 'Hours on YouTube', with: '100+ drills by level' },
  { label: 'Player skill levels', without: 'Wildly inconsistent', with: 'Logical progression' },
  { label: 'Tracking player progress', without: 'Guessing and memory', with: 'Documented portfolios' },
  { label: 'Parent conversations', without: 'Vague and uncomfortable', with: 'Specific data every block' },
  { label: 'In-game decisions', without: 'Freezing under pressure', with: '50 scenarios answered' },
  { label: 'Session quality', without: 'Always improvised', with: 'Structured & purposeful' },
  { label: 'Player retention', without: 'High dropout rate', with: 'Documented improvement = loyalty' },
]

export interface FaqItem {
  question: string
  answer: string
}

export const faqs: FaqItem[] = [
  {
    question: 'How does Baseball 360 work?',
    answer:
      'After your purchase you get instant access to all 15 volumes and 6 bonuses as downloadable PDFs. You choose your age category, run the Level Finder assessment to place your players in the right starting block, and follow the weekly practice sessions from there. Every session is fully scripted — just open the PDF and coach.',
  },
  {
    question: 'Does it work for any age group?',
    answer:
      'Yes. Baseball 360 covers 5 complete age categories: Minis (4–6), Infantil (7–9), Menores (10–12), Juveniles (13–15), and Mayores (16+). Each category has 3 volumes totaling 52 structured weeks. Whether you coach 5-year-olds or high school players, there is a complete curriculum designed specifically for that age group.',
  },
  {
    question: 'Can I use it if I coach multiple age groups?',
    answer:
      'Absolutely. The 15 volumes are organized so that you can run different categories simultaneously. Many coaches use Volumes 1–3 for their youngest group while running Volumes 7–9 for their older group. The Weekly Organizer (Bonus 5) is specifically designed for coaches managing multiple groups at once.',
  },
  {
    question: 'Are the bonuses really included?',
    answer:
      'Yes — all 6 bonuses are included in your $27 purchase at no extra cost. The Level Finder, Conditioning Routines, Nutrition Guide, Emergency Drills Toolkit, Weekly Organizer, and Enrollment Scripts are all delivered instantly with your main purchase.',
  },
  {
    question: 'Is it suitable for beginner coaches?',
    answer:
      'Baseball 360 was specifically designed so that a first-year coach with zero prior curriculum experience can open Volume 1 and run a professional, structured practice session on Day 1. No coaching certification required. The Level Finder tells you where to start and the volumes tell you exactly what to do from there.',
  },
  {
    question: 'Can I print the material?',
    answer:
      'Yes. All PDFs are print-ready in A4 format. You can print individual practice sheets, drill cards, assessment forms, and player portfolios. The Drill Card pages are specifically designed to be laminated and kept in your coaching bag for field use.',
  },
  {
    question: 'How long does access last?',
    answer:
      'Lifetime access. Once you purchase, the files are yours forever. Download them to your device and use them for as many seasons and as many players as you coach. No subscriptions. No renewals. One payment — lifetime access.',
  },
  {
    question: 'What makes this different from other coaching materials?',
    answer:
      'Most coaching resources give you isolated drills or general advice. Baseball 360 gives you a complete, documented, 52-week progression per age group — with player assessment tools, parent communication protocols, portfolio systems, and game-day tactical scenarios — all in one system. It is the difference between a collection of drills and a professional development institution.',
  },
]

export const purchaseNotifications = [
  { name: 'Carlos M.', location: 'Houston, Texas' },
  { name: 'Roberto G.', location: 'Monterrey, México' },
  { name: 'James P.', location: 'Tampa, Florida' },
  { name: 'Luis A.', location: 'Bogotá, Colombia' },
  { name: 'Michael D.', location: 'Phoenix, Arizona' },
  { name: 'Andrés V.', location: 'Caracas, Venezuela' },
  { name: 'Kevin R.', location: 'San Diego, California' },
  { name: 'José L.', location: 'San Juan, Puerto Rico' },
  { name: 'Brian K.', location: 'Dallas, Texas' },
  { name: 'Fernando C.', location: 'Santo Domingo, RD' },
]

export const checkoutUrl = 'https://baseball-360.impultienda.ar/checkout'

export const productImage =
  'https://cdn.magicpatterns.com/uploads/q2NxV5X5jSwEDqDPUX9KoJ/IMG_6182.png'
